import { slugify } from '@/utils/string.js';

/**
 * API error codes returned by statement transitions that the UI handles explicitly.
 * @enum {string}
 * @readonly
 */
export const StatementTransitionErrorCodes = Object.freeze({
  PROVIDER_UNCONFIRMED: 'provider_unconfirmed',
});

/**
 * How often (ms) and for how long (ms) to poll a statement after an unconfirmed provider response.
 */
export const FundConfirmationPollInterval = 3000;
export const FundConfirmationTimeout = 30000;

/**
 * Enum for statement statuses
 * @enum {string}
 * @readonly
 */
export const StatementStatus = Object.freeze({
  OPENED: 'OPENED',
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  CANCELED: 'CANCELED',
  FUNDED: 'FUNDED',
  PROCESSED: 'PROCESSED',
});

/**
 * Enum for statement statuses labels
 * @enum {string}
 * @readonly
 */
export const StatementStatusLabel = Object.freeze({
  [StatementStatus.OPENED]: 'Opened',
  [StatementStatus.REVIEWED]: 'Reviewed',
  [StatementStatus.APPROVED]: 'Approved',
  [StatementStatus.CANCELED]: 'Canceled',
  [StatementStatus.FUNDED]: 'Payment in progress',
  [StatementStatus.PROCESSED]: 'Processed',
});

/**
 * Encodes selected raw statement statuses as the snake_case labels the user picked, for use in
 * the URL query string (e.g. `FUNDED` -> `payment_in_progress`).
 * @param {string[]} statuses
 * @returns {string[]}
 */
export const encodeStatementStatusesForUrl = (statuses) =>
  statuses.map((status) => slugify(StatementStatusLabel[status])).filter(Boolean);

/**
 * Reverses encodeStatementStatusesForUrl: maps snake_case label slugs from the URL back to their
 * underlying raw status values. Unrecognized slugs are silently dropped.
 * @param {string|string[]} value
 * @returns {string[]}
 */
export const decodeStatementStatusesFromUrl = (value) => {
  const slugs = Array.isArray(value) ? value : [value];

  return slugs
    .map((slug) => Object.keys(StatementStatusLabel).find((status) => slugify(StatementStatusLabel[status]) === slug))
    .filter(Boolean);
};

/**
 * Enum for statement status badge style (daisyUI badge classes)
 * @enum {string}
 * @readonly
 */
export const StatementStatusBadgeStyle = Object.freeze({
  [StatementStatus.OPENED]: 'badge-dash',
  [StatementStatus.REVIEWED]: '',
  [StatementStatus.APPROVED]: 'badge badge-primary',
  [StatementStatus.FUNDED]: 'badge-dash badge-success',
  [StatementStatus.PROCESSED]: 'badge badge-success',
  [StatementStatus.CANCELED]: 'badge badge-error',
});

/**
 * Enum for statement status badge color (used by the status pill filter, which
 * renders on app-badge's `color` prop rather than the raw badge classes above)
 * @enum {string}
 * @readonly
 */
export const StatementStatusBadgeColor = Object.freeze({
  [StatementStatus.OPENED]: 'neutral',
  [StatementStatus.REVIEWED]: 'neutral',
  [StatementStatus.APPROVED]: 'primary',
  [StatementStatus.CANCELED]: 'error',
  [StatementStatus.FUNDED]: 'success',
  [StatementStatus.PROCESSED]: 'success',
});

/**
 * Enum for statement status badge icon
 * @enum {string}
 * @readonly
 */
export const StatementStatusIcon = Object.freeze({
  [StatementStatus.OPENED]: 'loading',
  [StatementStatus.REVIEWED]: 'check-square',
  [StatementStatus.APPROVED]: 'thumbs-up',
  [StatementStatus.CANCELED]: 'info-circle',
  [StatementStatus.FUNDED]: 'building-eight',
  [StatementStatus.PROCESSED]: 'check',
});

/**
 * Enum for statement file types
 * @enum {string}
 * @readonly
 */
export const StatementFileTypes = Object.freeze({
  REMITTANCE: 'REMITTANCE',
  PROFORMA: 'PROFORMA',
  INVOICE: 'INVOICE',
});

/**
 * Enum for statement file statuses
 * @enum {string}
 * @readonly
 */
export const StatementFileStatus = Object.freeze({
  PROCESSING: 'PROCESSING',
  FAILED: 'FAILED',
  SUCCEEDED: 'SUCCEEDED',
});

/**
 * Enum for statement transition event names
 * @enum {string}
 * @readonly
 */
export const StatementStatusTransitionEvents = Object.freeze({
  REJECT: 'REJECT',
  REVIEW: 'REVIEW',
  APPROVE: 'APPROVE',
  CANCEL: 'CANCEL',
  FUND: 'FUND',
  PROCESS: 'PROCESS',
});

/**
 * Enum for statement transition event icons
 * @enum {string}
 * @readonly
 */
export const StatementTransitionEventIcons = Object.freeze({
  [StatementStatusTransitionEvents.REJECT]: 'flip-backward',
  [StatementStatusTransitionEvents.REVIEW]: 'check-square',
  [StatementStatusTransitionEvents.APPROVE]: 'thumbs-up',
  [StatementStatusTransitionEvents.FUND]: 'building-eight',
});

/**
 * Enum for statement transition event button action copy
 * @enum {string}
 * @readonly
 */
export const StatementTransitionEventPrimaryActionCopy = Object.freeze({
  [StatementStatusTransitionEvents.REJECT]: 'Yes, undo review',
  [StatementStatusTransitionEvents.REVIEW]: 'Yes, mark as reviewed',
  [StatementStatusTransitionEvents.APPROVE]: 'Yes, approve it for payment',
  [StatementStatusTransitionEvents.FUND]: 'Initiate payment',
});

/**
 * Maps statement status strings to their sort order index.
 * Used to sort statements in progression order:
 * OPENED < REVIEWED < APPROVED < FUNDED < PROCESSED.
 *
 * @readonly
 * @enum {number}
 */
export const StatementStatusOrder = Object.freeze({
  OPENED: 0,
  REVIEWED: 1,
  APPROVED: 2,
  FUNDED: 3,
  PROCESSED: 4,
});
